import { CompanyData, INDUSTRY_SPECS, calcCompanyIncome, useGame } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";
import { getLandValueByLatLng } from "@/mocks/landValues";

interface Props {
  company: CompanyData;
  isOwner: boolean;
}

const LEVEL_LABELS = ["", "スタートアップ", "中小企業", "中堅企業", "大企業", "グローバル企業"];

export default function CompanyHeader({ company, isOwner }: Props) {
  const { isDark } = useTheme();
  const { blocks } = useGame();
  const spec = INDUSTRY_SPECS[company.industry];

  // 地価係数ベースの収益計算
  const landBlock = Object.values(blocks).find((b) => b.blockId === company.blockId);
  const blockLat = landBlock?.lat ?? 35.6812;
  const blockLng = landBlock?.lng ?? 139.7671;
  const lv = getLandValueByLatLng(blockLat, blockLng);
  const hourlyIncome = calcCompanyIncome(company.employees.length, company.products.filter(p => p.status === "active").length, blockLat, blockLng);
  const hasActivity = company.employees.length > 0 || company.products.filter(p => p.status === "active").length > 0;

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const divider = isDark ? "border-white/5" : "border-gray-100";

  const profitColor = company.finances.profit >= 0 ? "text-emerald-400" : "text-red-400";

  return (
    <div className={`rounded-2xl border p-6 mb-6 ${cardBg}`}>
      <div className="flex flex-col lg:flex-row items-start gap-6">
        {/* Company Icon */}
        <div className={`w-20 h-20 flex items-center justify-center rounded-2xl flex-shrink-0 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
          <i className={`${spec.icon} text-4xl ${spec.color}`}></i>
        </div>

        {/* Company Info */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className={`text-2xl font-black ${textPrimary}`}>{company.name}</h1>
            {isOwner && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-amber-400/15 text-amber-400 font-semibold whitespace-nowrap">
                オーナー
              </span>
            )}
            {company.isPublic && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-400/15 text-emerald-400 font-semibold whitespace-nowrap">
                上場企業
              </span>
            )}
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${isDark ? "bg-white/5 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
              Lv.{company.level} {LEVEL_LABELS[company.level]}
            </span>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <i className={`${spec.icon} text-sm ${spec.color}`}></i>
            <span className={`text-sm ${textSecondary}`}>{spec.label}</span>
            <span className={`text-sm ${textSecondary}`}>•</span>
            <span className={`text-sm ${textSecondary}`}>設立: {new Date(company.foundedAt).toLocaleDateString("ja-JP")}</span>
          </div>
          <p className={`text-sm ${textSecondary} mb-4`}>{company.description}</p>

          {/* Reputation Bar */}
          <div className="flex items-center gap-3">
            <span className={`text-xs ${textSecondary} whitespace-nowrap`}>評判</span>
            <div className={`flex-1 h-2 rounded-full ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
              <div
                className="h-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all"
                style={{ width: `${company.reputation}%` }}
              ></div>
            </div>
            <span className="text-xs text-amber-400 font-bold whitespace-nowrap">{company.reputation}/100</span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className={`grid grid-cols-2 gap-3 flex-shrink-0 w-full lg:w-auto`}>
          <div className={`rounded-xl p-3 text-center ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1 whitespace-nowrap`}>時間収益</p>
            <p className={`font-black text-lg whitespace-nowrap ${hourlyIncome > 0 ? "text-emerald-400" : "text-gray-500"}`}>
              {hourlyIncome > 0 ? `+${hourlyIncome}` : "0"}
            </p>
            <p className={`text-xs ${textSecondary}`}>coins/h</p>
          </div>
          <div className={`rounded-xl p-3 text-center ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1 whitespace-nowrap`}>地価係数</p>
            <p className="font-black text-lg whitespace-nowrap text-amber-400">×{lv.landMultiplier}</p>
            <p className={`text-xs ${textSecondary} whitespace-nowrap`}>{lv.flag} {lv.country}</p>
          </div>
          {!hasActivity && (
            <div className={`col-span-2 rounded-xl p-2 text-xs text-center ${isDark ? "bg-red-400/10 text-red-400 border border-red-400/20" : "bg-red-50 text-red-500 border border-red-200"}`}>
              <i className="ri-error-warning-line mr-1"></i>従業員・製品がないと収益ゼロ
            </div>
          )}
          <div className={`rounded-xl p-3 text-center ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1 whitespace-nowrap`}>従業員数</p>
            <p className={`font-black text-lg whitespace-nowrap ${textPrimary}`}>
              {company.employees.length}
            </p>
            <p className={`text-xs ${textSecondary}`}>/ {company.maxEmployees}名</p>
          </div>
          <div className={`rounded-xl p-3 text-center ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <p className={`text-xs ${textSecondary} mb-1 whitespace-nowrap`}>製品数</p>
            <p className={`font-black text-lg whitespace-nowrap ${textPrimary}`}>
              {company.products.filter((p) => p.status === "active").length}
            </p>
            <p className={`text-xs ${textSecondary}`}>アクティブ</p>
          </div>
        </div>
      </div>

      {/* Stock Info */}
      {company.isPublic && (
        <div className={`mt-4 pt-4 border-t ${divider} flex flex-wrap gap-6`}>
          <div>
            <p className={`text-xs ${textSecondary} mb-0.5`}>株価</p>
            <p className="text-amber-400 font-bold">{company.stockPrice.toLocaleString()} coins</p>
          </div>
          <div>
            <p className={`text-xs ${textSecondary} mb-0.5`}>発行済株式数</p>
            <p className={`font-bold ${textPrimary}`}>{company.totalShares.toLocaleString()}株</p>
          </div>
          <div>
            <p className={`text-xs ${textSecondary} mb-0.5`}>時価総額</p>
            <p className={`font-bold ${textPrimary}`}>{(company.stockPrice * company.totalShares).toLocaleString()} coins</p>
          </div>
          <div>
            <p className={`text-xs ${textSecondary} mb-0.5`}>総資産</p>
            <p className={`font-bold ${textPrimary}`}>{company.finances.totalAssets.toLocaleString()} coins</p>
          </div>
        </div>
      )}
    </div>
  );
}
